import { alpha, darken, lighten } from '@mui/material/styles';
import {
  DEFAULT_LANDING_PAGE_IMAGE_FIT,
  DEFAULT_LANDING_PAGE_IMAGE_FOCUS,
  isValidLandingPageUrl,
  type LandingPageBlock,
  type LandingPageBlockType,
  type LandingPageImageFit,
} from '@provision/landing-page-utils';
import { cleanColor, MIN_BUTTON_CONTRAST, surfaceFor, worstContrast } from './colors';
import { imageFitSx } from './image-fit';
import { LAYOUT_DEFINITIONS } from './layouts';
import { buildLayout } from './layouts/build-layout';
import type { LandingLayout, LandingTokens, Surface, Sx } from './layouts/types';

/** The surface each block's own styles are written for: text colours assume it. */
const NATURAL_SURFACE: Record<LandingPageBlockType, Surface> = {
  hero: 'dark',
  content: 'light',
  plans: 'light',
  faq: 'light',
  closingCta: 'dark',
};

const AUTO_LIGHTEN = 0.14;
const AUTO_DARKEN = 0.2;
const GRADIENT_ANGLE = '160deg';
// A wash over an image in the block's own tone, so its text reads whatever the photo holds.
const IMAGE_SCRIM: Record<Surface, number> = { dark: 0.6, light: 0.78 };

const BACKGROUND_SIZE: Record<LandingPageImageFit, string> = {
  contain: 'contain',
  cover: 'cover',
  fill: '100% 100%',
};

interface BlockBackground {
  css: string;
  surface: Surface;
  /** The band is close enough to the brand that a brand button would disappear into it. */
  brandBlends: boolean;
}

type BlockBackgroundInput = Partial<NonNullable<LandingPageBlock['background']>>;

/**
 * A block's own background as CSS, and which text reads on it. Undefined when the block has none
 * or its colour or image is unusable, so the layout's own background shows instead.
 */
export function resolveBlockBackground(
  background: BlockBackgroundInput,
  type: LandingPageBlockType,
  tokens: LandingTokens,
): BlockBackground | undefined {
  if (background.fill === 'image') return imageBackground(background, type, tokens);

  const color = cleanColor(background.color);
  if (!color) return undefined;

  const gradient = background.fill === 'autoGradient';
  const stops = gradient
    ? [lighten(color, AUTO_LIGHTEN), color, darken(color, AUTO_DARKEN)]
    : [color];

  return {
    css: gradient ? `linear-gradient(${GRADIENT_ANGLE}, ${stops.join(', ')})` : color,
    surface: surfaceFor(stops, tokens),
    brandBlends: worstContrast(tokens.brand, stops) < MIN_BUTTON_CONTRAST,
  };
}

// Inside a double-quoted CSS string only a quote, a backslash or a line break can end it early;
// percent-encoding those keeps the URL a URL.
const cssUrl = (url: string): string =>
  url.replace(/["\\\s]/g, (char) => encodeURIComponent(char));

// An image's tone is unknown, so the block keeps its own surface and the scrim guarantees it.
function imageBackground(
  {
    imageUrl,
    imageFit = DEFAULT_LANDING_PAGE_IMAGE_FIT,
    imageFocusX = DEFAULT_LANDING_PAGE_IMAGE_FOCUS,
    imageFocusY = DEFAULT_LANDING_PAGE_IMAGE_FOCUS,
  }: BlockBackgroundInput,
  type: LandingPageBlockType,
  t: LandingTokens,
): BlockBackground | undefined {
  const url = imageUrl?.trim();
  if (!url || !isValidLandingPageUrl(url)) return undefined;

  const surface = NATURAL_SURFACE[type];
  const base = surface === 'dark' ? t.ink : t.paper;
  const scrim = alpha(base, IMAGE_SCRIM[surface]);
  const { objectPosition } = imageFitSx(imageFit, imageFocusX, imageFocusY);

  return {
    css: [
      `linear-gradient(${scrim}, ${scrim})`,
      `url("${cssUrl(url)}") ${objectPosition} / ${BACKGROUND_SIZE[imageFit]} no-repeat`,
      base,
    ].join(', '),
    surface,
    // The wash sets the button apart from whatever the photo holds.
    brandBlends: false,
  };
}

// Light-surface styles put text in `ink`/`muted` and rules in `line`; on a dark band those read
// as their light counterparts. `paper` stands in for the FAQ's expanded icon glyph.
const onDarkTokens = (t: LandingTokens): Partial<LandingTokens> => ({
  ink: t.onDark,
  muted: alpha(t.onDark, 0.72),
  line: alpha(t.onDark, 0.24),
  lineSoft: alpha(t.onDark, 0.14),
  brandInk: lighten(t.brand, 0.28),
  paper: t.ink,
});

// Dark-surface styles put text in `onDark` and tints derived from it.
const onLightTokens = (t: LandingTokens): Partial<LandingTokens> => ({ onDark: t.ink });

/**
 * The layout a block renders with on its own background: the page's, restyled for the opposite
 * surface when the background calls for it, and with the block's own band art removed.
 */
export function buildBlockLayout(
  page: LandingLayout,
  type: LandingPageBlockType,
  { surface, brandBlends }: BlockBackground,
): LandingLayout {
  const t = page.tokens;
  let layout = page;
  if (surface !== NATURAL_SURFACE[type]) {
    const flip = surface === 'dark' ? onDarkTokens(t) : onLightTokens(t);
    layout = buildLayout(LAYOUT_DEFINITIONS[page.id], { ...t, ...flip });
  }

  const styles = layout[type];
  layout = {
    ...layout,
    [type]: { ...styles, section: { ...styles.section, background: 'none' } },
  };

  if (type === 'hero' && surface === 'light') {
    // The hero's eyebrow and savings tick are brand tints that only read on a dark band.
    layout = {
      ...layout,
      hero: {
        ...layout.hero,
        eyebrow: { ...layout.hero.eyebrow, color: t.brandInk },
        noteIcon: { ...layout.hero.noteIcon, color: t.brandInk },
      },
    };
  }

  return brandBlends ? withOffBrandButton(layout, surface) : layout;
}

/**
 * The layout with its brand button restyled to stand off a background the brand matches: white
 * on a dark one, ink on a light one.
 */
export function withOffBrandButton(layout: LandingLayout, surface: Surface): LandingLayout {
  const t = layout.tokens;
  const { button } = layout.shared;
  const brand: Sx =
    surface === 'dark'
      ? button.light
      : {
          ...button.brand,
          backgroundColor: t.ink,
          color: t.paperHi,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 12px 26px -10px ${alpha(t.ink, 0.45)}`,
          },
        };

  return { ...layout, shared: { ...layout.shared, button: { ...button, brand } } };
}
